import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Baby, Loader2, Plus } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { LossForm } from '../../components/LossForm';
import { SFHChart } from '../../components/SFHChart';
import type { LossRecord, OBVisitSummary, Pregnancy } from '../../types/pregnancy';
import type { ClinicalNote } from '../../types/clinical';
import { pregnancyDetailReducer, initialState } from './reducer';

function gestationToday(lmp: string): string {
  if (!lmp) return '';
  const diff = Date.now() - new Date(lmp).getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days < 0) return '';
  return `${Math.floor(days / 7)}w ${days % 7}d`;
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  delivered: 'bg-blue-100 text-blue-800',
  miscarriage: 'bg-gray-100 text-gray-800',
  terminated: 'bg-gray-100 text-gray-800',
};

export function PregnancyDetailPage() {
  const { patientId, pregId } = useParams<{ patientId: string; pregId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { get, post } = useApi();
  const { userRole } = useAuth();
  const isDoctor = userRole === 'doctor';

  // Use navigation state from the list page for instant render
  const navState = location.state as { pregnancy?: Pregnancy; patientName?: string } | null;

  const [s, dispatch] = useReducer(pregnancyDetailReducer, initialState);
  const [pregnancy, setPregnancy] = useState<Pregnancy | null>(navState?.pregnancy || null);
  const [visits, setVisits] = useState<OBVisitSummary[]>([]);
  const [consultations, setConsultations] = useState<ClinicalNote[]>([]);
  const [lossRecord, setLossRecord] = useState<LossRecord | null>(null);
  const [loading, setLoading] = useState(!navState?.pregnancy);
  const [error, setError] = useState('');
  const [patientName, setPatientName] = useState(navState?.patientName || '');

  const fetchData = useCallback(async () => {
    if (!patientId || !pregId) return;
    setLoading(true);
    try {
      const [pregRes, visitsRes, patRes] = await Promise.all([
        get<Pregnancy>(`/patients/${patientId}/pregnancies/${pregId}`),
        get<{ visits: OBVisitSummary[] }>(`/patients/${patientId}/pregnancies/${pregId}/visits`),
        get<{ firstName: string; lastName: string }>(`/patients/${patientId}`),
      ]);
      if (pregRes.success && pregRes.data) setPregnancy(pregRes.data);
      else setError(pregRes.error || 'Failed to load pregnancy');
      if (visitsRes.success && visitsRes.data) setVisits(visitsRes.data.visits);
      if (patRes.success && patRes.data) setPatientName(`${patRes.data.firstName || ''} ${patRes.data.lastName || ''}`.trim());
    } catch {
      setError('Failed to load pregnancy data');
    } finally {
      setLoading(false);
    }
  }, [patientId, pregId, get]);

  // Loss record fetched separately — never blocks page render (404 is expected for most pregnancies)
  const fetchLoss = useCallback(async () => {
    if (!patientId || !pregId) return;
    try {
      const lossRes = await get<LossRecord>(`/patients/${patientId}/pregnancies/${pregId}/loss`);
      if (lossRes.success && lossRes.data) setLossRecord(lossRes.data);
    } catch {
      // 404 is expected — no loss record for this pregnancy
    }
  }, [patientId, pregId, get]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchLoss(); }, [fetchLoss]);

  const handleSaveLoss = useCallback(async (data: Record<string, unknown>) => {
    if (!patientId || !pregId) return;
    const res = await post<LossRecord>(`/patients/${patientId}/pregnancies/${pregId}/loss`, data);
    if (res.success && res.data) {
      setLossRecord(res.data);
      dispatch({ type: 'CLOSE_LOSS_MISCARRIAGE' });
      dispatch({ type: 'CLOSE_LOSS_TERMINATION' });
      await fetchData();
      await fetchLoss();
    } else {
      throw new Error(res.error || 'Failed to save');
    }
  }, [patientId, pregId, post, fetchData, fetchLoss]);

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-teal-600" /></div>;
  }

  if (!pregnancy) {
    return <div className="max-w-5xl mx-auto px-4 py-6"><p className="text-gray-500">Pregnancy not found.</p></div>;
  }

  return (
    <main className="max-w-5xl mx-auto px-4 py-6">
      {/* Breadcrumb */}
      <nav className="text-xs text-gray-500 mb-2 flex items-center gap-1">
        <button onClick={() => navigate(`/clinical/${patientId}`)} className="hover:text-blue-600">Clinical</button>
        <span>&rsaquo;</span>
        <button onClick={() => navigate(`/patients/${patientId}/pregnancies`)} className="hover:text-blue-600">Pregnancies</button>
        <span>&rsaquo;</span>
        <span className="text-gray-700">EDD {pregnancy.edd}</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/patients/${patientId}/pregnancies`)} className="p-1 text-gray-400 hover:text-gray-600">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Pregnancy — EDD {pregnancy.edd}</h1>
            <div className="flex items-center gap-3 mt-0.5 text-sm text-gray-500">
              {patientName && <span>{patientName}</span>}
              {pregnancy.status === 'active' && pregnancy.lmp && <span>{gestationToday(pregnancy.lmp)} gestation</span>}
              <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_COLORS[pregnancy.status]}`}>
                {pregnancy.status.charAt(0).toUpperCase() + pregnancy.status.slice(1)}
              </span>
              {pregnancy.rhesusStatus && (
                <span className={`px-2 py-0.5 text-xs rounded-full ${pregnancy.rhesusStatus === 'negative' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-gray-50 text-gray-700 border border-gray-200'}`}>
                  Rh {pregnancy.rhesusStatus === 'positive' ? '+' : '−'}
                </span>
              )}
            </div>
          </div>
        </div>
        {isDoctor && (
          <div className="flex items-center gap-3">
            {pregnancy.status === 'active' && (
              <button
                onClick={() => navigate(`/clinical/${patientId}`, { state: { openObsForm: true, pregnancyId: pregId } })}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 flex items-center gap-2"
              >
                <Plus className="h-4 w-4" /> New Obstetric Consult
              </button>
            )}
            <button
              onClick={() => navigate(`/patients/${patientId}/pregnancies`)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2"
            >
              <Plus className="h-4 w-4" /> New Pregnancy
            </button>
          </div>
        )}
      </div>

      {error && <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex gap-6">
          {(['gravidogram', 'consultations', 'outcome'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => dispatch({ type: 'SET_TAB', tab })}
              className={`pb-2 text-sm font-medium border-b-2 transition-colors ${s.activeTab === tab ? 'border-teal-600 text-teal-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              {tab === 'gravidogram' ? 'Gravidogram' : tab === 'consultations' ? 'Consultations' : 'Outcome'}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {s.activeTab === 'gravidogram' && (
        <div>
          <div className="mb-6">
            <SFHChart visits={visits} />
          </div>

          {/* Visit summary table */}
          {visits.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Gestation</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">BP</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">SFH</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">FHR</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Presentation</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {visits.map(v => (
                    <tr key={v.noteId} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-900">{v.date}</td>
                      <td className="px-3 py-2 text-gray-500">{v.gestationWeeks != null ? `${v.gestationWeeks}w${v.gestationDays ? ` ${v.gestationDays}d` : ''}` : '—'}</td>
                      <td className="px-3 py-2 text-gray-500">{v.bpSystolic && v.bpDiastolic ? `${v.bpSystolic}/${v.bpDiastolic}` : '—'}</td>
                      <td className="px-3 py-2 text-gray-500">{v.sfh != null ? `${v.sfh}cm` : '—'}</td>
                      <td className="px-3 py-2 text-gray-500">{v.fetalHeartRate ? `${v.fetalHeartRate}bpm` : '—'}</td>
                      <td className="px-3 py-2 text-gray-500 capitalize">{v.presentation || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {s.activeTab === 'consultations' && (
        <div>
          {visits.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No obstetric consultations recorded yet.</p>
          ) : (
            <div className="space-y-2">
              {visits.map(v => (
                <div key={v.noteId} className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">
                      {v.date} — {v.gestationWeeks != null ? `${v.gestationWeeks}w${v.gestationDays ? `${v.gestationDays}d` : ''}` : ''}
                    </span>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      {v.sfh != null && <span>SFH: {v.sfh}cm</span>}
                      {v.bpSystolic && <span>BP: {v.bpSystolic}/{v.bpDiastolic}</span>}
                      {v.fetalHeartRate && <span>FHR: {v.fetalHeartRate}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {s.activeTab === 'outcome' && (
        <div>
          {pregnancy.status === 'active' && (
            <div className="space-y-3">
              <div className="flex gap-3">
                <button onClick={() => navigate(`/clinical/${patientId}`, { state: { openDeliveryForm: true, pregnancyId: pregId } })} className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700">
                  Record Delivery — Vaginal
                </button>
                <button onClick={() => navigate(`/clinical/${patientId}`, { state: { openSurgeryForm: true, pregnancyId: pregId } })} className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700">
                  Record Delivery — Caesarean
                </button>
              </div>
              <div className="flex gap-3">
                <button onClick={() => dispatch({ type: 'OPEN_LOSS_MISCARRIAGE' })} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                  Record Miscarriage
                </button>
                <button onClick={() => dispatch({ type: 'OPEN_LOSS_TERMINATION' })} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                  Record Termination
                </button>
              </div>
            </div>
          )}

          {pregnancy.status === 'delivered' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-medium text-blue-800">
                Delivered via {pregnancy.deliveryRecordType === 'vaginal_delivery' ? 'Vaginal Delivery' : 'Caesarean Section'}
              </p>
              <button
                onClick={() => navigate(`/clinical/${patientId}`)}
                className="mt-2 text-sm text-blue-600 hover:underline"
              >
                View in {pregnancy.deliveryRecordType === 'vaginal_delivery' ? 'Deliveries' : 'Surgeries'}
              </button>
            </div>
          )}

          {(pregnancy.status === 'miscarriage' || pregnancy.status === 'terminated') && lossRecord && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-800 mb-2">
                {pregnancy.status === 'miscarriage' ? 'Miscarriage' : 'Termination'} — {lossRecord.date}
              </p>
              <div className="text-xs text-gray-600 space-y-1">
                <p>Gestation: {lossRecord.gestationWeeks}w {lossRecord.gestationDays}d</p>
                {lossRecord.management && <p>Management: {lossRecord.management}</p>}
                {lossRecord.procedure && <p>Procedure: {lossRecord.procedure}</p>}
                {lossRecord.notes && <p>Notes: {lossRecord.notes}</p>}
              </div>
            </div>
          )}

          {s.showLossMiscarriage && (
            <div className="mt-4">
              <LossForm
                type="miscarriage"
                rhesusStatus={pregnancy.rhesusStatus}
                onSave={handleSaveLoss}
                onCancel={() => dispatch({ type: 'CLOSE_LOSS_MISCARRIAGE' })}
              />
            </div>
          )}
          {s.showLossTermination && (
            <div className="mt-4">
              <LossForm
                type="termination"
                rhesusStatus={pregnancy.rhesusStatus}
                onSave={handleSaveLoss}
                onCancel={() => dispatch({ type: 'CLOSE_LOSS_TERMINATION' })}
              />
            </div>
          )}
        </div>
      )}
    </main>
  );
}
